// API справочника (005: contracts/api.md, «Маршруты»; data-model.md). Обработчики — дорожка S1.
// Почту в ответах видят роли с directory.write (FR-037): без права полей email и emails нет.
import { z } from '../kernel/zod.ts';
import {
  DateSchema,
  IdSchema,
  OperationIdSchema,
  PersonRefSchema,
  TimestampSchema,
  VersionSchema,
} from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';
import { ALIAS_TEXT_MAX, AliasSchema, AliasScopeSchema } from '../directory/alias.ts';
import { AddresseeReasonSchema, AddresseeSchema } from '../directory/addressee.ts';
import {
  MentionColumnSchema,
  MentionKindSchema,
  MentionRowSchema,
  MentionStateSchema,
} from '../directory/mention.ts';
import { MergeSchema, PairAnswerSchema, PairSchema } from '../directory/pair.ts';
import { ManagerSchema, UnitNameInputSchema, UnitNodeSchema } from '../directory/unit.ts';
import {
  KindChangeSchema,
  KindDoubtReasonSchema,
  RecordKeySchema,
  RecordKindChangeSchema,
} from '../directory/kind-change.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import {
  CuratorshipSchema,
  EmailSchema,
  ExternalTypeSchema,
  ORGANIZATION_NAME_MAX,
  OrganizationSchema,
  OrganizationSideSchema,
  PERSON_NAME_MAX,
  POSITION_MAX,
  PersonSchema,
  RecordKindSchema,
  RecordRefSchema,
  RecordStateSchema,
  WorkplaceSchema,
  ZoneSchema,
} from '../directory/record.ts';

export const DIRECTORY_SEARCH_MAX = 200;

/** Да или нет в строке запроса: «true»/«false» — stringbool, а не coerce (как в 003). */
const flag = z.stringbool().optional();

/** Фильтр по адресату: есть или нет (FR-034, FR-034а). */
export const addresseeFilters = ['ready', 'missing'] as const;
export const AddresseeFilterSchema = z.enum(addresseeFilters);

/** Фильтр по почте: нет годной почты или внешний адрес не подтверждён (FR-034а). */
export const emailFilters = ['missing', 'unconfirmed'] as const;
export const EmailFilterSchema = z.enum(emailFilters);

export const recordSortFields = ['name', 'openItems', 'nearestDue', 'mentions'] as const;

/** Список справочника (FR-009): условия — в адресе страницы. */
export const RecordsQuerySchema = PageQuerySchema.extend({
  kind: RecordKindSchema.default('person'),
  /** Поиск без учёта регистра по ФИО, названию и псевдонимам. */
  q: z.string().trim().min(1).max(DIRECTORY_SEARCH_MAX).optional(),
  /** Состояния через запятую: draft,confirmed. По умолчанию — без архива и склеенных. */
  state: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(RecordStateSchema))
    .optional(),
  side: OrganizationSideSchema.optional(),
  addressee: AddresseeFilterSchema.optional(),
  email: EmailFilterSchema.optional(),
  /** С открытыми пунктами. */
  openItems: flag,
  /** Организации без основного куратора. */
  noPrimaryCurator: flag,
  /** Люди этой организации (место работы) — группа и фильтр «Организация». */
  organizationId: IdSchema.optional(),
  documentId: IdSchema.optional(),
  /** «Новое из последней загрузки» (FR-034а, FR-034б). */
  newSinceUpload: flag,
  /** Только записи с парами «тот же?» без ответа. */
  withPairs: flag,
  /** «Вид — решение ИИ» (005-3, FR-012): черновики с упоминаниями с пометкой, без «Вид верный». */
  kindByAi: flag,
  sort: sortSchema(recordSortFields).default('name'),
});
export type RecordsQuery = z.output<typeof RecordsQuerySchema>;

/** Почта в строке: основной адрес, внешний ли, подтверждён ли. */
export const EmailBriefSchema = z.object({
  address: z.string(),
  external: z.boolean(),
  confirmed: z.boolean(),
});

/** Адресат записи для таблицы: есть или причина (FR-033). */
export const AddresseeBriefSchema = z.object({
  ready: z.boolean(),
  reason: AddresseeReasonSchema.nullable(),
});
export type AddresseeBrief = z.infer<typeof AddresseeBriefSchema>;

/** Строка справочника (FR-009). */
/**
 * Получатель пунктов человека «без почты» (FR-010, уточнение 2): руководитель или, если он сам
 * не годный адресат, основной куратор организации.
 */
export const NoEmailToSchema = z.object({
  to: RecordRefSchema,
  via: z.enum(['manager', 'curator']),
});
export type NoEmailTo = z.infer<typeof NoEmailToSchema>;

export const RecordRowSchema = z.object({
  record: RecordRefSchema,
  state: RecordStateSchema,
  /** Версия записи: массовое действие пропускает изменённую после загрузки списка (REC-001). */
  version: VersionSchema,
  /** У организации. */
  side: OrganizationSideSchema.nullable(),
  externalType: ExternalTypeSchema.nullable(),
  /** У человека: организация и должность основного места работы. */
  organization: RecordRefSchema.nullable(),
  position: z.string().nullable(),
  /** Есть только у ролей с directory.write; null — почты нет. */
  email: EmailBriefSchema.nullable().optional(),
  /** У организации: основной куратор. */
  primaryCurator: RecordRefSchema.nullable(),
  mentions: z.number().int().min(0),
  openItems: z.number().int().min(0),
  nearestDue: DateSchema.nullable(),
  addressee: AddresseeBriefSchema,
  /** Пары «тот же?» без ответа. */
  pairs: z.number().int().min(0),
  /** Появилась в последней загрузке. */
  isNew: z.boolean(),
  /** У человека: «без почты» — пункты руководителю (006, FR-010). */
  noEmail: z.boolean(),
  /** Кому уходят пункты человека «без почты» (FR-010): руководителю или куратору; иначе null. */
  noEmailTo: NoEmailToSchema.nullable(),
});
export type RecordRow = z.infer<typeof RecordRowSchema>;

export const RecordsPageSchema = pageSchema(RecordRowSchema);

/** Открытые пункты записи: число и ближайший срок; сами пункты — в «Контроле» по responsibleId. */
export const OpenItemsBriefSchema = z.object({
  count: z.number().int().min(0),
  nearestDue: DateSchema.nullable(),
});

/** Упоминание, по которому вид записи определил ИИ (005-3, FR-011). */
export const KindByAiSchema = z.object({
  text: z.string(),
  column: MentionColumnSchema,
  probability: z.number().min(0).max(1),
  at: TimestampSchema,
});
export type KindByAi = z.infer<typeof KindByAiSchema>;

/** Карточка человека (FR-010). */
export const PersonCardSchema = z.object({
  person: PersonSchema,
  emails: z.array(EmailSchema).optional(),
  workplaces: z.array(WorkplaceSchema),
  /** Организации, которые человек курирует. */
  curatorships: z.array(CuratorshipSchema),
  aliases: z.array(AliasSchema),
  mentions: z.number().int().min(0),
  openItems: OpenItemsBriefSchema,
  /** Пары «тот же?» без ответа. */
  pairs: z.array(PairSchema),
  /** Склейки, где запись главная и которые не разъединены, — «Разъединить» (FR-026). */
  merges: z.array(MergeSchema),
  /** Последняя невозвращённая смена вида, где запись прежняя или цель (005-2, FR-009). */
  kindChange: RecordKindChangeSchema.nullable(),
  /** Пользователь может сменить вид записи (005-2, FR-019; ведущему — свой черновик). */
  kindChangeable: z.boolean(),
  /** Вид определил ИИ (005-3, FR-011): первое упоминание с пометкой «вид — ИИ». */
  kindByAi: KindByAiSchema.nullable(),
  /** «Без почты» — пункты руководителю (006, FR-010); suggestClear — почта годна, отметку можно снять. */
  noEmail: z.boolean(),
  noEmailSuggestClear: z.boolean(),
  /** Руководитель по дереву подразделений (006, FR-008, FR-009). */
  manager: ManagerSchema,
  /** Кому уходят пункты при «без почты» (FR-010); отметки нет или адресата нет — null. */
  noEmailTo: NoEmailToSchema.nullable(),
  addressee: AddresseeBriefSchema,
});
export type PersonCard = z.infer<typeof PersonCardSchema>;

/** Карточка организации (FR-010, FR-017): кураторы, сотрудники, документы зоны. */
export const OrganizationCardSchema = z.object({
  organization: OrganizationSchema,
  curators: z.array(CuratorshipSchema),
  employees: z.array(WorkplaceSchema),
  aliases: z.array(AliasSchema),
  /** Своя организация: документы, где она — зона ответственности. */
  zoneDocuments: z.array(
    z.object({ documentId: IdSchema, number: z.number().int().min(1), title: z.string() }),
  ),
  mentions: z.number().int().min(0),
  openItems: OpenItemsBriefSchema,
  pairs: z.array(PairSchema),
  merges: z.array(MergeSchema),
  kindChange: RecordKindChangeSchema.nullable(),
  kindChangeable: z.boolean(),
  kindByAi: KindByAiSchema.nullable(),
  /** Подразделения организации — дерево плоским списком (006, FR-001, FR-009). */
  units: z.array(UnitNodeSchema),
  addressee: AddresseeBriefSchema,
});
export type OrganizationCard = z.infer<typeof OrganizationCardSchema>;

export const PersonNameInputSchema = z
  .string({ error: fieldMessages.fullName })
  .trim()
  .min(2, { error: fieldMessages.fullName })
  .max(PERSON_NAME_MAX, { error: fieldMessages.fullName });

export const OrganizationNameInputSchema = z
  .string({ error: fieldMessages.organizationName })
  .trim()
  .min(2, { error: fieldMessages.organizationName })
  .max(ORGANIZATION_NAME_MAX, { error: fieldMessages.organizationName });

export const PositionInputSchema = z
  .string({ error: fieldMessages.position })
  .trim()
  .max(POSITION_MAX, { error: fieldMessages.position })
  .nullable();

export const CreatePersonBodySchema = z.object({
  fullName: PersonNameInputSchema,
  /** Сразу с местом работы — основным. */
  organizationId: IdSchema.optional(),
  position: PositionInputSchema.optional(),
});

export type CreatePersonBody = z.output<typeof CreatePersonBodySchema>;

export const CreateOrganizationBodySchema = z.object({
  name: OrganizationNameInputSchema,
  side: OrganizationSideSchema,
  externalType: ExternalTypeSchema.nullable().optional(),
});

export type CreateOrganizationBody = z.output<typeof CreateOrganizationBodySchema>;

/** Состояние, которое меняет человек: подтвердить черновик, в архив, вернуть из архива. */
export const editableRecordStates = ['confirmed', 'archived'] as const;
export const EditableRecordStateSchema = z.enum(editableRecordStates);

const atLeastOne = (value: Record<string, unknown>): boolean =>
  Object.values(value).some((v) => v !== undefined);

export const UpdatePersonBodySchema = z
  .object({
    fullName: PersonNameInputSchema.optional(),
    state: EditableRecordStateSchema.optional(),
    notAddressee: z.boolean().optional(),
    /** «Без почты» (006, FR-010). */
    noEmail: z.boolean().optional(),
  })
  .refine(atLeastOne);

export const UpdateOrganizationBodySchema = z
  .object({
    name: OrganizationNameInputSchema.optional(),
    side: OrganizationSideSchema.optional(),
    externalType: ExternalTypeSchema.nullable().optional(),
    state: EditableRecordStateSchema.optional(),
    notAddressee: z.boolean().optional(),
  })
  .refine(atLeastOne);

/** Формат почты проверяет сервер и отвечает DIR-001 (FR-013): поле — как ввёл человек. */
export const SetEmailBodySchema = z.object({ address: z.string().trim().min(1).max(254) });

export const SetEmailResponseSchema = z.object({
  card: PersonCardSchema,
  /** Тот же адрес уже у другого человека: предупреждение, пара «тот же?» ушла в «К разбору». */
  sameEmail: RecordRefSchema.nullable(),
});
export type SetEmailResponse = z.infer<typeof SetEmailResponseSchema>;

export const ReceivesMailBodySchema = z.object({ receivesMail: z.boolean() });

export const CreateWorkplaceBodySchema = z.object({
  personId: IdSchema,
  organizationId: IdSchema,
  /** Подразделение организации (006, FR-006). */
  unitId: IdSchema.nullable().optional(),
  position: PositionInputSchema.optional(),
  /** Новое основное снимает отметку с прежнего. */
  primary: z.boolean().optional(),
});

export type CreateWorkplaceBody = z.output<typeof CreateWorkplaceBodySchema>;

export const UpdateWorkplaceBodySchema = z
  .object({
    position: PositionInputSchema.optional(),
    primary: z.literal(true).optional(),
    /** Подразделение организации места работы; null — без подразделения (006, FR-006). */
    unitId: IdSchema.nullable().optional(),
  })
  .refine(atLeastOne);

export const CreateCuratorshipBodySchema = z.object({
  personId: IdSchema,
  organizationId: IdSchema,
  /** Объект кураторства — подразделение этой организации (006, FR-007). */
  unitId: IdSchema.nullable().optional(),
  primary: z.boolean().optional(),
});

export type CreateCuratorshipBody = z.output<typeof CreateCuratorshipBodySchema>;

/** Снимается основной куратор — нужен новый основной из оставшихся, иначе DIR-003 (FR-019). */
export const RemoveCuratorshipBodySchema = z.object({ newPrimaryId: IdSchema.optional() });

export const ZoneParamsSchema = z.object({ documentId: IdSchema });

export const SetZoneBodySchema = z.object({
  legalEntityId: IdSchema,
  /** Подразделение юрлица зоны (006, FR-016). */
  unitId: IdSchema.nullable().optional(),
});

export const ZoneListSchema = z.object({ items: z.array(ZoneSchema) });

export const CreateAliasBodySchema = z.object({
  recordId: IdSchema,
  text: z
    .string({ error: fieldMessages.aliasText })
    .trim()
    .min(1, { error: fieldMessages.aliasText })
    .max(ALIAS_TEXT_MAX, { error: fieldMessages.aliasText }),
  scope: AliasScopeSchema,
});

export const MentionsQuerySchema = PageQuerySchema.extend({
  /** Ждут решения контролёра: вид «неясно» или связь с вопросом (FR-006, FR-016; T045). */
  pending: flag,
  recordId: IdSchema.optional(),
  kind: MentionKindSchema.optional(),
  state: MentionStateSchema.optional(),
  documentId: IdSchema.optional(),
  newSinceUpload: flag,
});

export const MentionsPageSchema = pageSchema(MentionRowSchema);

/**
 * Решение по упоминанию (FR-006, FR-007а, сценарий 1, п. 5): связать с записью (псевдоним
 * подтверждается в контексте упоминания), новая запись этого вида, «не адресат».
 */
export const ResolveMentionBodySchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('link'), recordId: IdSchema }),
  z.object({ action: z.literal('new'), kind: RecordKindSchema }),
  z.object({ action: z.literal('not_addressee') }),
  /** «Подразделение…» (006, FR-018): существующее или новое подразделение организации зоны. */
  z.object({
    action: z.literal('unit'),
    unitId: IdSchema.optional(),
    newUnit: z
      .object({
        organizationId: IdSchema,
        parentId: IdSchema.nullable(),
        name: UnitNameInputSchema,
      })
      .optional(),
  }),
]);

export const PairsQuerySchema = PageQuerySchema.extend({
  kind: RecordKindSchema.optional(),
  recordId: IdSchema.optional(),
});

export const PairsPageSchema = pageSchema(PairSchema);

/** Сторона окна сравнения (FR-025): запись, упоминания, места работы, почта (по праву). */
export const PairSideSchema = z.object({
  record: RecordRefSchema,
  state: RecordStateSchema,
  mentions: z.array(MentionRowSchema),
  mentionsTotal: z.number().int().min(0),
  workplaces: z.array(WorkplaceSchema),
  emails: z.array(EmailSchema).optional(),
});

export const PairCardSchema = z.object({
  pair: PairSchema,
  sides: z.tuple([PairSideSchema, PairSideSchema]),
});
export type PairCard = z.infer<typeof PairCardSchema>;

export const CreatePairBodySchema = z.object({ aId: IdSchema, bId: IdSchema });

/** «Тот же» склеивает: главная — mainId (по умолчанию a пары). */
export const PairDecisionBodySchema = z.object({
  answer: PairAnswerSchema,
  mainId: IdSchema.optional(),
});

export const PairDecisionResultSchema = z.object({
  pair: PairSchema,
  /** Склейка при «тот же» — для тоста «Отменить». */
  merge: MergeSchema.nullable(),
});
export type PairDecisionResult = z.infer<typeof PairDecisionResultSchema>;

/** «К разбору» (FR-034а) — числа ссылками в справочник с фильтром. */
export const TriageCountsSchema = z.object({
  pairs: z.number().int().min(0),
  unclear: z.number().int().min(0),
  /** Связи с вопросом: однофамильцы, архив, переведён, контекст (FR-007, FR-007а). */
  questions: z.number().int().min(0),
  noAddressee: z.number().int().min(0),
  unconfirmedEmails: z.number().int().min(0),
  noPrimaryCurator: z.number().int().min(0),
  documentsWithoutZone: z.number().int().min(0),
  /** Вопросы «Похоже на другой вид» (005-2, FR-018). */
  kindDoubts: z.number().int().min(0),
  /** Подразделения без основного куратора (006, FR-013). */
  unitsWithoutCurator: z.number().int().min(0),
});

export const TRIAGE_MISSING_LIMIT = 10;

/**
 * «Готовы к рассылке N из M» (FR-034): записи с открытыми пунктами; кому не хватает —
 * по числу открытых пунктов и ближайшему сроку, первые TRIAGE_MISSING_LIMIT.
 */
export const ReadinessSchema = z.object({
  ready: z.number().int().min(0),
  total: z.number().int().min(0),
  missing: z
    .array(
      z.object({
        record: RecordRefSchema,
        reason: AddresseeReasonSchema,
        openItems: z.number().int().min(0),
        nearestDue: DateSchema.nullable(),
      }),
    )
    .max(TRIAGE_MISSING_LIMIT),
});

export const TriageSchema = z.object({
  counts: TriageCountsSchema,
  /** Сумма counts — число у пункта меню «Справочник» (FR-009). */
  total: z.number().int().min(0),
  readiness: ReadinessSchema,
});
export type Triage = z.infer<typeof TriageSchema>;

export const DIRECTORY_SEARCH_LIMIT = 20;

/** Поиск ⌘K (FR-034в): от двух знаков. */
export const DirectorySearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(DIRECTORY_SEARCH_MAX),
  limit: z.coerce.number().int().min(1).max(DIRECTORY_SEARCH_LIMIT).default(DIRECTORY_SEARCH_LIMIT),
});

export const DirectorySearchResponseSchema = z.object({
  items: z.array(
    z.object({
      record: RecordRefSchema,
      state: RecordStateSchema,
      organization: RecordRefSchema.nullable(),
      /** Нашлось по псевдониму — его текст. */
      alias: z.string().nullable(),
    }),
  ),
});

export const VersionSummaryParamsSchema = z.object({ versionId: IdSchema });

/** Состояние связывания версии: ждёт, идёт, готово, упало (задача directory.link). */
export const linkStates = ['queued', 'linking', 'linked', 'failed'] as const;
export const LinkStateSchema = z.enum(linkStates);

/** Итог разбора версии в справочнике (FR-034б). */
export const VersionDirectorySummarySchema = z.object({
  versionId: IdSchema,
  state: LinkStateSchema,
  newRecords: z.number().int().min(0),
  newPairs: z.number().int().min(0),
  unclear: z.number().int().min(0),
  linkedAt: TimestampSchema.nullable(),
  /** Связывание упало: код и номер операции задачи (принцип V). */
  error: z.object({ code: ErrorCodeSchema, operation: OperationIdSchema }).nullable(),
});
export type VersionDirectorySummary = z.infer<typeof VersionDirectorySummarySchema>;

/** Ответственный пункта в «Контроле» (FR-011, FR-033): упоминание с записью и адресатом. */
export const ControlResponsibleSchema = z.object({
  mentionId: IdSchema,
  text: z.string(),
  kind: MentionKindSchema,
  record: RecordRefSchema.nullable(),
  addressee: AddresseeSchema,
});
export type ControlResponsible = z.infer<typeof ControlResponsibleSchema>;

// 005-2: смена вида записи и вопросы о виде (contracts/api.md 005-2, «Схемы»).

/** Окно последствий смены вида (FR-004): вид и номер записи, новый вид. */
export const KindChangePreviewQuerySchema = z.object({
  kind: RecordKindSchema,
  id: IdSchema,
  to: RecordKindSchema,
});

const count = z.number().int().min(0);

/** Данные, которых нет у нового вида: останутся в истории прежней записи (FR-008). */
export const KindKeptSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('person'),
    emails: count,
    workplaces: count,
    curatorships: count,
  }),
  z.object({
    kind: z.literal('organization'),
    employees: count,
    curators: count,
    side: OrganizationSideSchema,
    externalType: ExternalTypeSchema.nullable(),
    receivesMail: z.boolean(),
  }),
]);

/** Цель объединения (FR-006): запись другого вида с тем же ключом имени или псевдонима. */
export const KindCandidateSchema = z.object({
  ref: RecordRefSchema,
  side: OrganizationSideSchema.nullable(),
  state: RecordStateSchema,
  mentions: count,
});

export const KindChangePreviewSchema = z.object({
  /** Черновик без кандидатов и без FR-003а — смена сразу, без окна (FR-003). */
  direct: z.boolean(),
  /** Человек → организация с открытыми пунктами: имя перестанет скрываться в вопросах ИИ (FR-003а). */
  maskingWarning: z.boolean(),
  mentions: count,
  documents: count,
  openItems: count,
  /** У скольких открытых пунктов сменится адресат (FR-033 005). */
  addresseeChanges: count,
  kept: KindKeptSchema,
  /** Ведущему — пусто: объединение — склейка (FR-019). */
  candidates: z.array(KindCandidateSchema),
});
export type KindChangePreview = z.infer<typeof KindChangePreviewSchema>;

export const KindChangeBodySchema = z.object({
  record: RecordKeySchema,
  /** Версия записи из карточки: другой пользователь изменил — REC-001 (FR-020). */
  version: VersionSchema,
  to: RecordKindSchema,
  /** Цель объединения или null — новая запись. */
  targetId: IdSchema.nullable(),
});
export type KindChangeBody = z.output<typeof KindChangeBodySchema>;

/** Смена вида и карточка: цели — после смены, прежней записи — после возврата. */
export const KindChangeResultSchema = z.object({
  change: KindChangeSchema,
  card: z.union([PersonCardSchema, OrganizationCardSchema]),
});
export type KindChangeResult = z.infer<typeof KindChangeResultSchema>;

export const KindDoubtsQuerySchema = PageQuerySchema;

/** Вопрос «Похоже на организацию» или «Похоже на человека» (FR-015). */
export const KindDoubtSchema = z.object({
  record: RecordRefSchema.extend({ state: RecordStateSchema, version: VersionSchema }),
  /** Вид, на который похожа запись. */
  suggested: RecordKindSchema,
  reason: KindDoubtReasonSchema,
  /** Вероятность «организация» по оценке ИИ (005-3, FR-014, FR-015); модель не спрашивали — null. */
  probability: z.number().min(0).max(1).nullable(),
  /** Строка протокола одного из упоминаний; у записи без упоминаний — null. */
  mention: MentionRowSchema.nullable(),
});
export type KindDoubt = z.infer<typeof KindDoubtSchema>;

export const KindDoubtsPageSchema = pageSchema(KindDoubtSchema);
export type KindDoubtsPage = z.infer<typeof KindDoubtsPageSchema>;

/** «Вид верный» (FR-016). */
export const KindReviewBodySchema = z.object({ record: RecordKeySchema });

export const KindReviewSchema = z.object({
  record: RecordRefSchema,
  at: TimestampSchema,
  by: PersonRefSchema,
});
export type KindReview = z.infer<typeof KindReviewSchema>;

// --- Массовые действия (006: FR-023 – FR-026; research, Р9) ---

export const BULK_MAX = 200;

export const bulkActions = ['confirm', 'kind', 'not_addressee', 'workplace'] as const;
export type BulkAction = (typeof bulkActions)[number];
export const BulkActionSchema = z.enum(bulkActions);

export const bulkActionLabels = {
  confirm: 'Подтвердить',
  kind: 'Сменить вид',
  not_addressee: 'Не адресат',
  workplace: 'Место работы',
} as const satisfies Record<BulkAction, string>;

/** Одно действие над набором записей; idempotencyKey — повтор запроса не применяет дважды. */
export const BulkBodySchema = z
  .object({
    idempotencyKey: z.string().trim().min(8).max(100),
    action: BulkActionSchema,
    /**
     * Больше BULK_MAX — DIR-015 (проверяет сервер, чтобы отказ был с кодом). version — из строки
     * списка: запись изменили после загрузки — пропуск REC-001 («обновите список»).
     */
    records: z.array(RecordKeySchema.extend({ version: VersionSchema.optional() })).min(1),
    to: RecordKindSchema.optional(),
    workplace: z
      .object({
        organizationId: IdSchema,
        /** Нет — подразделение прежнего места в этой организации не меняется; null — убрать. */
        unitId: IdSchema.nullable().optional(),
        position: PositionInputSchema.optional(),
      })
      .optional(),
  })
  .refine((v) => v.action !== 'kind' || v.to !== undefined, {
    path: ['to'],
    error: 'Выберите вид',
  })
  .refine((v) => v.action !== 'workplace' || v.workplace !== undefined, {
    path: ['workplace'],
    error: 'Выберите организацию',
  });
export type BulkBody = z.output<typeof BulkBodySchema>;

export const BulkResultSchema = z.object({
  id: IdSchema,
  action: BulkActionSchema,
  done: z.array(RecordKeySchema),
  /** Пропуск — код ошибки без сведений о записи (FR-025а). */
  skipped: z.array(z.object({ record: RecordKeySchema, code: ErrorCodeSchema })),
  undoneAt: TimestampSchema.nullable(),
});
export type BulkResult = z.infer<typeof BulkResultSchema>;

export const BulkParamsSchema = z.object({ id: IdSchema });
